import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Mail, ArrowLeft, ShieldCheck, Loader2, Lock, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

type OtpStep = "email" | "otp";
type PasswordMode = "login" | "register";

const Auth = () => {
  const [authTab, setAuthTab] = useState<"otp" | "password">("password");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1 flex items-center justify-center bg-hero p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="rounded-xl border border-border bg-card p-8 shadow-hero">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-9 h-9 rounded-lg bg-gradient-gold flex items-center justify-center">
                <FileText className="w-4.5 h-4.5 text-accent-foreground" />
              </div>
              <span className="font-display font-bold text-xl">DocSeal</span>
            </div>

            <Tabs value={authTab} onValueChange={(v) => setAuthTab(v as "otp" | "password")} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="password">
                  <Lock className="w-3.5 h-3.5 mr-1.5" />密码登录
                </TabsTrigger>
                <TabsTrigger value="otp">
                  <Mail className="w-3.5 h-3.5 mr-1.5" />验证码登录
                </TabsTrigger>
              </TabsList>

              <TabsContent value="password">
                <PasswordAuth />
              </TabsContent>
              <TabsContent value="otp">
                <OtpAuth />
              </TabsContent>
            </Tabs>
          </div>

          <p className="text-center text-xs text-primary-foreground/40 mt-6">
            <Link to="/" className="hover:text-primary-foreground/60">← 返回首页</Link>
          </p>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

/* ─── Password Login / Register ─── */
const PasswordAuth = () => {
  const [mode, setMode] = useState<PasswordMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) throw error;
        toast({ title: "登录成功" });
        navigate("/dashboard");
      } else {
        const { error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        toast({
          title: "注册成功",
          description: "请查看邮箱完成验证后登录。",
        });
        setMode("login");
      }
    } catch (err: any) {
      toast({ title: mode === "login" ? "登录失败" : "注册失败", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="font-display font-bold text-2xl text-center mb-1">
        {mode === "login" ? "账号登录" : "注册账号"}
      </h1>
      <p className="text-sm text-muted-foreground text-center mb-5">
        {mode === "login" ? "使用邮箱和密码登录" : "创建新账号"}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pw-email">邮箱地址</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="pw-email"
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9"
              required
              autoFocus
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw-password">密码</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="pw-password"
              type="password"
              placeholder={mode === "register" ? "至少 6 位密码" : "输入密码"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-9"
              required
              minLength={6}
            />
          </div>
        </div>

        <Button type="submit" variant="hero" className="w-full" disabled={loading}>
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" />{mode === "login" ? "登录中..." : "注册中..."}</>
          ) : mode === "login" ? "登 录" : "注 册"}
        </Button>
      </form>

      <div className="mt-4 flex items-center justify-between text-xs">
        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="text-accent hover:underline inline-flex items-center gap-1"
        >
          {mode === "login" ? (
            <><UserPlus className="w-3 h-3" /> 没有账号？注册</>
          ) : (
            <><Lock className="w-3 h-3" /> 已有账号？登录</>
          )}
        </button>
        {mode === "login" && (
          <Link to="/reset-password" className="text-muted-foreground hover:text-foreground">
            忘记密码？
          </Link>
        )}
      </div>
    </div>
  );
};

/* ─── OTP Login ─── */
const OtpAuth = () => {
  const [step, setStep] = useState<OtpStep>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-otp", {
        body: { email: email.trim() },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setStep("otp");
      setCountdown(60);
      toast({ title: "验证码已发送", description: "请查看邮箱中的 6 位验证码。" });
    } catch (err: any) {
      toast({ title: "发送失败", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-otp", {
        body: { email: email.trim(), code: otp },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const { error: verifyErr } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash,
        type: "magiclink",
      });
      if (verifyErr) throw verifyErr;
      toast({ title: "登录成功" });
      navigate("/dashboard");
    } catch (err: any) {
      toast({ title: "验证失败", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-otp", {
        body: { email: email.trim() },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCountdown(60);
      toast({ title: "验证码已重新发送" });
    } catch (err: any) {
      toast({ title: "发送失败", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (step === "otp") {
    return (
      <div>
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-accent" />
          </div>
        </div>
        <h1 className="font-display font-bold text-2xl text-center mb-1">输入验证码</h1>
        <p className="text-sm text-muted-foreground text-center mb-6">
          已发送至 <span className="font-medium text-foreground">{email}</span>
        </p>
        <form onSubmit={handleVerifyOtp} className="space-y-5">
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otp} onChange={setOtp}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button type="submit" variant="hero" className="w-full" disabled={loading || otp.length < 6}>
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> 验证中...</> : "验证并登录"}
          </Button>
        </form>
        <div className="mt-5 flex items-center justify-between text-xs">
          <button onClick={() => { setStep("email"); setOtp(""); }} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> 更换邮箱
          </button>
          <button onClick={handleResend} disabled={countdown > 0} className={countdown > 0 ? "text-muted-foreground/50 cursor-not-allowed" : "text-accent hover:underline"}>
            {countdown > 0 ? `重新发送 (${countdown}s)` : "重新发送"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-display font-bold text-2xl text-center mb-1">验证码登录</h1>
      <p className="text-sm text-muted-foreground text-center mb-5">
        输入邮箱，我们将发送 6 位验证码完成登录
      </p>
      <form onSubmit={handleSendOtp} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="otp-email">邮箱地址</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="otp-email"
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-9"
              required
            />
          </div>
        </div>
        <Button type="submit" variant="hero" className="w-full" disabled={loading}>
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> 发送中...</> : "获取验证码"}
        </Button>
      </form>
      <p className="text-center text-xs text-muted-foreground mt-5">
        无需密码，验证码直连登录。首次登录自动创建账户。
      </p>
    </div>
  );
};

export default Auth;
